/**
 * Lightweight entry point for collection names and aliases.
 * Does NOT import schemas, builders, or Drizzle — safe for client-side use.
 *
 * Frontend files should import from here instead of the main barrel:
 *   import { CollectionName, collectionAlias } from "@tc/search-document-builders/collection-names"
 */

/** All Typesense collection names — duplicated here to avoid importing the heavy registry */
export enum CollectionName {
  Agency = "agency",
  BottleSizes = "bottle-sizes",
  BusinessAreas = "business-areas",
  Caption = "caption",
  CertificateDeliveryMethods = "certificate-delivery-methods",
  CertificateTypes = "certificate-types",
  CnCodes = "cn-codes",
  CommodityTypes = "commodity-types",
  Countries = "countries",
  DeAlcoholised = "de-alcoholised",
  DestinationMarkets = "destination-markets",
  DocumentTypes = "document-types",
  Eligibilities = "eligibilities",
  EstarLaboratories = "estar-laboratories",
  EstarOrganisations = "estar-organisations",
  EstarWineTests = "estar-wine-tests",
  EUIngredients = "eu-wine-ingredients",
  ExportCertificates = "export-certificates",
  Exporters = "exporters",
  Field = "field",
  FreeSalePremises = "free-sale-premises",
  HsCodes = "hs-codes",
  ImportCertificates = "import-certificates",
  ImportCertificateTypes = "import-certificate-types",
  IppcAcronyms = "ippc-acronyms",
  Item = "item",
  ItemHistory = "item-history",
  Language = "language",
  List = "reference-lists",
  OcaUsers = "oca-users",
  OrganicPremises = "organic-premises",
  PackageTypes = "package-types",
  Pesticides = "pesticides",
  PhytoBotanicalName = "phyto-botanical-name",
  Port = "port",
  Process = "process",
  ProcessGroup = "process-group",
  ProcessGroupCaption = "process-group-caption",
  ProcessTypes = "process-types",
  ProductFields = "product-fields",
  Products = "products",
  Registrations = "registrations",
  SupportingDocuments = "supporting-documents",
  Templates = "templates",
  TransferProducts = "transfer-products",
  TransferRequests = "transfer-requests",
  Unit = "unit",
  UnLocode = "un-locode",
  Verifiers = "verifiers",
  WineBrClassifications = "wine-br-classifications",
  WineChemTests = "wine-chem-tests",
  WineColours = "wine-colours",
  WineEUCategories = "wine-eu-categories",
  WineGeographicIndications = "wine-geographic-indications",
  WineGrapeVarieties = "wine-grape-varieties",
  WineLabDetail = "wine-lab-detail",
  WineSpecialProcesses = "wine-special-processes",
  WineStyles = "wine-styles",
  Wsmps = "wsmps",
}

/** Private collections — all others default to "public" */
const PRIVATE_COLLECTIONS: ReadonlySet<string> = new Set([
  CollectionName.Agency,
  CollectionName.ExportCertificates,
  CollectionName.Language,
  CollectionName.OcaUsers,
  CollectionName.Products,
  CollectionName.Registrations,
  CollectionName.SupportingDocuments,
  CollectionName.Templates,
  CollectionName.TransferRequests,
])

/**
 * Get the prefixed alias for a collection, e.g. "public:port" or "private:registrations".
 * Lightweight version — does not import schema-registry or any builder files.
 */
export const collectionAlias = (name: string): string => {
  const prefix = PRIVATE_COLLECTIONS.has(name) ? "private" : "public"
  return `${prefix}:${name}`
}
